// stores/configurationStore.ts
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { ActionFlowConfigurationDTO } from '../../api';

interface ConfigurationState {
    // Data
    configurations: ActionFlowConfigurationDTO[];
    activeConfiguration: ActionFlowConfigurationDTO | null;
    searchTerm: string;

    // UI State
    isLoading: boolean;
    error: Error | null;

    // Actions
    setConfigurations: (configurations: ActionFlowConfigurationDTO[]) => void;
    setActiveConfiguration: (configuration: ActionFlowConfigurationDTO | null) => void;
    addConfiguration: (configuration: ActionFlowConfigurationDTO) => void;
    updateConfiguration: (configuration: ActionFlowConfigurationDTO) => void;
    removeConfiguration: (id: string) => void;
    setSearchTerm: (term: string) => void;
    setLoading: (isLoading: boolean) => void;
    setError: (error: Error | null) => void;
    reset: () => void;
}

export const useConfigurationStore = create<ConfigurationState>()(
    devtools(
        (set) => ({
            // Initial state
            configurations: [],
            activeConfiguration: null,
            searchTerm: '',
            isLoading: false,
            error: null,

            // Actions
            setConfigurations: (configurations) =>
                set({ configurations }),

            setActiveConfiguration: (configuration) =>
                set({ activeConfiguration: configuration }),

            addConfiguration: (configuration) =>
                set((state) => ({
                    configurations: [...state.configurations, configuration]
                })),

            updateConfiguration: (configuration) =>
                set((state) => ({
                    configurations: state.configurations.map((config) =>
                        config.id === configuration.id ? configuration : config
                    ),
                    activeConfiguration:
                        state.activeConfiguration?.id === configuration.id
                            ? configuration
                            : state.activeConfiguration
                })),

            removeConfiguration: (id) =>
                set((state) => ({
                    configurations: state.configurations.filter(
                        (config) => config.id !== id
                    ),
                    activeConfiguration:
                        state.activeConfiguration?.id === id
                            ? null
                            : state.activeConfiguration
                })),

            setSearchTerm: (term) =>
                set({ searchTerm: term }),

            setLoading: (isLoading) =>
                set({ isLoading }),

            setError: (error) =>
                set({ error }),

            reset: () =>
                set({
                    configurations: [],
                    activeConfiguration: null,
                    searchTerm: '',
                    isLoading: false,
                    error: null
                })
        }),
        { name: 'ConfigurationStore' }
    )
);

// Selectors for common state queries
export const configurationSelectors = {
    getFilteredConfigurations: (state: ConfigurationState) => {
        const searchLower = state.searchTerm.toLowerCase();
        return state.configurations.filter(config =>
            config.name?.toLowerCase().includes(searchLower) ||
            config.description?.toLowerCase().includes(searchLower) ||
            config.version?.toLowerCase().includes(searchLower)
        );
    },

    getConfigurationById: (state: ConfigurationState, id: string) =>
        state.configurations.find(config => config.id === id),

    getActiveConfiguration: (state: ConfigurationState) =>
        state.activeConfiguration,

    getIsLoading: (state: ConfigurationState) =>
        state.isLoading,

    getError: (state: ConfigurationState) =>
        state.error
};

// Store utilities for derived state
export const configurationUtils = {
    filterConfigurationsByVersion: (
        configurations: ActionFlowConfigurationDTO[],
        version: string
    ) => configurations.filter(config => config.version === version),

    filterConfigurationsByStatus: (
        configurations: ActionFlowConfigurationDTO[],
        status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED'
        // @ts-ignore
    ) => configurations.filter(config => config.status === status),

    sortConfigurationsByDate: (
        configurations: ActionFlowConfigurationDTO[],
        order: 'asc' | 'desc' = 'desc'
    ) => [...configurations].sort((a, b) => {
        // @ts-ignore
        const dateA = new Date(a.createdAt).getTime();
        // @ts-ignore
        const dateB = new Date(b.createdAt).getTime();
        return order === 'asc' ? dateA - dateB : dateB - dateA;
    })
};